/**
 * Overlay Configuration Types
 * 
 * Defines the structure for video overlay configurations that will be
 * processed by the video_overlay_enhancements worker.
 */

export interface ScoreOverlayConfig {
  enabled: boolean;
  preset: 'preset1' | 'preset2'; // Design template only (not position-specific)
  position: 'top-left' | 'top-right' | 'top' | 'bottom-left' | 'bottom-right' | 'bottom'; // Position options
  size?: 'xs' | 'sm' | 'md' | 'lg'; // Size options for mobile/desktop, Default: 'md'
  colors: {
    primary: string;     // Default: "#0B66FF" (brand blue)
    secondary: string;  // Default: "#FFFFFF" (white)
    textColor: string;  // Default: "#111827" (dark gray)
  };
  opacity: number;      // 0.0 to 1.0, Default: 1.0
  gradient: {
    enabled: boolean;   // Default: false
    type: 'horizontal' | 'vertical' | 'radial'; // Default: 'horizontal'
    direction?: number; // For horizontal/vertical: angle in degrees (0-360), Default: 0
    startColor: string; // Default: primary color
    endColor: string;    // Default: secondary color
    stops?: number[];   // For radial: [centerX%, centerY%], Default: [50, 50]
  };
  showTournamentLogo: boolean;  // Tournament logo in center between scores
  showMatchTime: boolean;       // Show match minute (e.g., "45'") from match_video_tags
  updateOnGoals: boolean;       // true = dynamic from match_video_tags goal events, false = static final score from matches table
}

export interface WatermarkConfig {
  enabled: boolean;
  logoType?: 'mark' | 'full'; // 'mark' = favicon/icon only, 'full' = logo with text
  logoPath?: string; // Optional - for future custom logos
  logoData?: string; // SVG XML string - populated when logoPath is empty (defaults to Kridah Labs logo)
  position: 'bottom-right' | 'bottom-left' | 'bottom' | 'top-right' | 'top-left' | 'top';
  opacity: number; // 0.0 to 1.0
  size: 'small' | 'medium' | 'large';
}

export interface IntroClipConfig {
  enabled: boolean;
  duration: number; // 3-7 seconds, default: 5
  preset: 'preset1' | 'preset2'; // 'preset1' = Simple, 'preset2' = Detailed
  
  // Design configuration
  textColor: string;   // Default: "#FFFFFF" (white) - Color of all text overlays
  backgroundColor: string; // Default: "#000000" (black) - Base background color
  opacity: number;        // 0.0 to 1.0, Default: 1.0
  gradient: {
    enabled: boolean;   // Default: false
    type: 'horizontal' | 'vertical' | 'radial';
    direction?: number; // For horizontal/vertical: 0-360 degrees, Default: 0
    startColor: string; // Default: backgroundColor
    endColor: string;    // Default: textColor (for contrast)
    stops?: number[];   // For radial: [centerX%, centerY%], Default: [50, 50]
  };
  
  // Content-specific settings (for 'preset2' - Detailed)
  showTournamentLogo: boolean;  // Default: true (for preset2)
  showTeamLogos: boolean;        // Default: true (for preset2)
  showLineups: boolean;         // Default: true (for preset2, if players exist)
}

export interface OutroClipConfig {
  enabled: boolean;
  duration: number; // 2-5 seconds, default: 3
  
  // Design configuration
  textColor: string;   // Default: "#FFFFFF" (white) - Color of all text overlays
  backgroundColor: string; // Default: "#000000" (black) - Base background color
  opacity: number;       // 0.0 to 1.0, Default: 1.0
  gradient: {
    enabled: boolean;
    type: 'horizontal' | 'vertical' | 'radial';
    direction?: number;  // 0-360 degrees
    startColor: string; // Default: backgroundColor
    endColor: string;    // Default: textColor (for contrast)
    stops?: number[];   // [centerX%, centerY%] for radial
  };
  
  // Content settings
  showTournamentLogo: boolean;  // Default: true (tournament logo and name)
  showThanksMessage: boolean;    // Default: true
  customMessage?: string;        // Optional custom "Thanks" message
}

export interface IntroOutroConfig {
  intro?: IntroClipConfig;
  outro?: OutroClipConfig;
}

// Legacy types (kept for reference, not used)
// export interface StartAnimationConfig {
//   enabled: boolean;
//   type: 'fade-in' | 'slide-in' | 'zoom-in';
//   duration: number; // seconds
//   content?: 'brand-intro' | 'context-setting' | 'custom';
// }

// export interface EndAnimationConfig {
//   enabled: boolean;
//   type: 'fade-out' | 'slide-out';
//   duration: number; // seconds
//   content?: 'brand-visibility' | 'cta' | 'custom';
// }

// export interface AnimationsConfig {
//   start?: StartAnimationConfig;
//   end?: EndAnimationConfig;
// }

export interface OverlayConfig {
  aspectRatio?: 'original' | '16:9' | '9:16' | '1:1' | '4:3'; // Video aspect ratio
  scoreOverlay?: ScoreOverlayConfig;
  watermark?: WatermarkConfig;
  introOutro?: IntroOutroConfig; // Intro/outro clips (Phase 1-3)
}

/**
 * Default overlay configuration
 */
export const defaultOverlayConfig: OverlayConfig = {
  aspectRatio: 'original', // Default: keep original aspect ratio
  scoreOverlay: {
    enabled: false,
    preset: 'preset1',
    position: 'top-right',
    size: 'md', // Default medium size
    colors: {
      primary: '#0B66FF',   // Brand blue
      secondary: '#FFFFFF', // White
      textColor: '#111827', // Dark gray
    },
    opacity: 1.0,
    gradient: {
      enabled: false,
      type: 'horizontal',
      direction: 0,
      startColor: '#0B66FF',
      endColor: '#FFFFFF',
      stops: [50, 50],
    },
    showTournamentLogo: false,
    showMatchTime: true,
    updateOnGoals: true,
  },
  watermark: {
    enabled: false,
    logoType: 'mark', // Default to favicon/icon
    position: 'bottom-right',
    opacity: 0.8,
    size: 'medium',
  },
  introOutro: {
    intro: {
      enabled: false,
      duration: 5,
      preset: 'preset1',
      textColor: '#FFFFFF',
      backgroundColor: '#000000',
      opacity: 1.0,
      gradient: {
        enabled: false,
        type: 'horizontal',
        direction: 0,
        startColor: '#000000',
        endColor: '#FFFFFF',
        stops: [50, 50],
      },
      showTournamentLogo: true,
      showTeamLogos: true,
      showLineups: true,
    },
    outro: {
      enabled: false,
      duration: 3,
      textColor: '#FFFFFF',
      backgroundColor: '#000000',
      opacity: 1.0,
      gradient: {
        enabled: false,
        type: 'horizontal',
        direction: 0,
        startColor: '#000000',
        endColor: '#FFFFFF',
        stops: [50, 50],
      },
      showTournamentLogo: true,
      showThanksMessage: true,
    },
  },
};
